import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2 } from "lucide-react";
import { LogsDialog } from "./LogsDialog";
import { toast } from "sonner";
import { useWorkspacesQuery } from "@/hooks/useWorkspacesQuery";
import { useMQTTTokens } from "@/context/MQTTTokensProvider";
import MQTTService, {
  type WorkspaceCommandLogMessage,
  type WorkspaceCommandResult,
} from "@/services/MQTTService";
import type { LogEntry } from "./LogsDialog";

interface DeleteWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  automationServerId: string;
  workspaceId: string;
  workspaceName: string;
}

export function DeleteWorkspaceModal({
  open,
  onOpenChange,
  automationServerId,
  workspaceId,
  workspaceName,
}: DeleteWorkspaceModalProps) {
  const [confirmationName, setConfirmationName] = useState("");
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const hasShownToast = useRef(false);
  const isNameValid = confirmationName === workspaceName;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Query workspaces to refetch after deletion
  const { refetch: refetchWorkspaces } = useWorkspacesQuery();

  // Get MQTT tokens refetch function to update connections after deletion
  const { refetch: refetchMQTTTokens } = useMQTTTokens();

  // State for workspace deletion using global MQTTService
  const [logs, setLogs] = useState<WorkspaceCommandLogMessage[]>([]);
  const [result, setResult] = useState<WorkspaceCommandResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const clearLogs = useCallback(() => {
    setLogs([]);
    setResult(null);
  }, []);

  const handleDelete = async () => {
    if (!isNameValid) return;
    if (!automationServerId) {
      console.error("Cannot delete workspace: No automation server ID");
      return;
    }

    clearLogs();
    setIsLoading(true);

    try {
      // Use the global MQTTService for workspace deletion
      const mqttService = MQTTService.getInstance();
      const deleteResult = await mqttService.deleteWorkspace(
        automationServerId,
        {
          id: workspaceId,
          name: workspaceName,
        },
        (log) => {
          // Handle log messages
          setLogs((prev) => [...prev, log]);
        }
      );

      console.log(
        "✅ DeleteWorkspaceModal: Workspace deletion completed:",
        deleteResult
      );
      setResult(deleteResult);
      setIsLoading(false);
    } catch (error: any) {
      console.error(
        "❌ DeleteWorkspaceModal: Error deleting workspace:",
        error
      );
      setResult({
        "request-id": "",
        success: false,
        error: error?.message || "Failed to delete workspace",
      });
      setIsLoading(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isLoading) {
      onOpenChange(false);
      // Reset form after a delay to allow dialog to close
      setTimeout(() => {
        setConfirmationName("");
        clearLogs();
      }, 300);
    }
  }, [isLoading, onOpenChange, clearLogs]);

  // Close on success, navigate, and show toast
  useEffect(() => {
    if (result?.success && !isLoading && !hasShownToast.current) {
      hasShownToast.current = true;

      // Refetch workspaces and MQTT tokens to sync after deletion
      const syncAndNavigate = async () => {
        // Wait a bit for workspace sync to complete
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Refetch MQTT tokens to update connections (remove deleted workspace)
        await refetchMQTTTokens();

        // Refetch workspaces
        const refetchedResult = await refetchWorkspaces();
        const allWorkspaces = refetchedResult.data ?? [];

        // Wait for AutomationsProvider to re-initialize MQTTService with updated tokens/workspaces
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Verify workspace is deleted (should not be in the list)
        const deletedWorkspace = allWorkspaces.find(
          (ws) =>
            ws.id === workspaceId ||
            (ws.name === workspaceName &&
              ws.automation_server === automationServerId)
        );

        if (!deletedWorkspace) {
          // Workspace successfully deleted, proceed with navigation
          toast.success(`Workspace "${workspaceName}" deleted successfully`);
          // Navigate to workspaces page
          navigate("/workspaces");
          // Close modal and reset form
          handleClose();
        } else {
          // Workspace still found, try again after a delay
          setTimeout(async () => {
            // Refetch MQTT tokens again in case they weren't ready
            await refetchMQTTTokens();
            const retryResult = await refetchWorkspaces();
            const retryWorkspaces = retryResult.data ?? [];
            const stillExists = retryWorkspaces.find(
              (ws) =>
                ws.id === workspaceId ||
                (ws.name === workspaceName &&
                  ws.automation_server === automationServerId)
            );

            if (!stillExists) {
              // Wait for AutomationsProvider to re-initialize MQTTService with updated tokens/workspaces
              await new Promise((resolve) => setTimeout(resolve, 1000));

              // Workspace now deleted, proceed with navigation
              toast.success(`Workspace "${workspaceName}" deleted successfully`);
              navigate("/workspaces");
              handleClose();
            } else {
              // Still exists, show toast and close modal anyway (deletion may be in progress)
              toast.success(`Workspace "${workspaceName}" deletion initiated`);
              navigate("/workspaces");
              handleClose();
            }
          }, 2000);
        }
      };

      syncAndNavigate();
    }
  }, [
    result?.success,
    isLoading,
    workspaceName,
    workspaceId,
    automationServerId,
    onOpenChange,
    navigate,
    queryClient,
    clearLogs,
    refetchWorkspaces,
    refetchMQTTTokens,
    handleClose,
  ]);

  // Reset toast flag when modal closes
  useEffect(() => {
    if (!open) {
      hasShownToast.current = false;
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Delete Workspace
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete the
            workspace <strong>&quot;{workspaceName}&quot;</strong> and remove
            all source code and configurations from that workspace.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-900 font-semibold mb-2">
              ⚠️ Warning: This will permanently delete:
            </p>
            <ul className="text-sm text-red-800 list-disc list-inside space-y-1">
              <li>All source code in the workspace</li>
              <li>All configurations and settings</li>
              <li>All automations and pipelines</li>
              <li>All associated data</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmation-name">
              To confirm deletion, type the workspace name:{" "}
              <strong>&quot;{workspaceName}&quot;</strong>
            </Label>
            <Input
              id="confirmation-name"
              placeholder={workspaceName}
              value={confirmationName}
              onChange={(e) => setConfirmationName(e.target.value)}
              className={
                confirmationName && !isNameValid ? "border-red-500" : ""
              }
              disabled={isLoading}
            />
            {confirmationName && !isNameValid && (
              <p className="text-sm text-red-600">
                Workspace name does not match. Please enter the exact name.
              </p>
            )}
          </div>

          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-16 w-16 animate-spin text-primary mb-4" />
              <p className="text-sm text-muted-foreground mb-2">
                Deleting workspace...
              </p>
              {(logs.length > 0 || isLoading) && (
                <button
                  type="button"
                  onClick={() => setShowLogsDialog(true)}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  show logs...
                </button>
              )}
            </div>
          )}

          {!isLoading && logs.length > 0 && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setShowLogsDialog(true)}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                show logs...
              </button>
            </div>
          )}

          {result && (
            <div
              className={`p-3 rounded-md ${
                result.success
                  ? "bg-green-50 text-green-900 border border-green-200"
                  : "bg-red-50 text-red-900 border border-red-200"
              }`}
            >
              {result.success ? (
                <div>
                  ✓ {result.message || "Workspace deleted successfully"}
                </div>
              ) : (
                <div>✗ {result.error || "Workspace deletion failed"}</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {result?.success ? "Close" : "Cancel"}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isNameValid || isLoading || !automationServerId}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Workspace"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      <LogsDialog
        open={showLogsDialog}
        onOpenChange={setShowLogsDialog}
        logs={logs as LogEntry[]}
        isLoading={isLoading}
        title="Workspace Deletion Logs"
      />
    </Dialog>
  );
}
